// Чистые функции вкладки «Шаги»: дни с целью, сводка периода, серии, рекорды, ритм недели, календарь за год.
// Цель дня — цель Garmin этого дня (steps_goal пишется по дням); до первой записанной — первая известная;
// цели нет совсем — текущая цель Garmin, иначе 10 000. День с 0 шагов — часы не носили, его как бы нет.

import { MONTHS_SHORT } from '@/lib/format'
import { addDays, parseIsoDate, toIsoDate } from '@/lib/periods'
import type { Point } from '@/pages/metrics/api'
import { WAN_STEPS } from '@/pages/today/logic'

export const DEFAULT_GOAL = WAN_STEPS

export type StepDay = { day: string; steps: number; goal: number }

/** Дни с шагами по возрастанию даты, у каждого — своя цель. */
export function stepDays(steps: Point[], goals: Point[], current: number | null): StepDay[] {
  const known = goals.filter((g) => g.value > 0).sort((a, b) => a.day.localeCompare(b.day))
  const fallback = known[0]?.value ?? (current && current > 0 ? current : DEFAULT_GOAL)
  let i = -1
  return steps
    .filter((p) => p.value > 0)
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((p) => {
      while (i + 1 < known.length && known[i + 1]!.day <= p.day) i += 1
      return { day: p.day, steps: Math.round(p.value), goal: i >= 0 ? known[i]!.value : fallback }
    })
}

export const goalMet = (d: StepDay) => d.steps >= d.goal

export type PeriodSummary = { days: number; total: number; average: number | null; goalDays: number }

/** Сводка за дни [start, end]; start = null — с самого начала. */
export function periodSummary(days: StepDay[], start: string | null, end: string): PeriodSummary {
  const inside = days.filter((d) => (start === null || d.day >= start) && d.day <= end)
  const total = inside.reduce((sum, d) => sum + d.steps, 0)
  return {
    days: inside.length,
    total,
    average: inside.length ? Math.round(total / inside.length) : null,
    goalDays: inside.filter(goalMet).length,
  }
}

export type Streaks = {
  /** Дней подряд с выполненной целью до сегодня; сегодняшний день ещё не кончился и серию не рвёт. */
  current: number
  /** Сегодня цель уже выполнена и входит в текущую серию. */
  todayIncluded: boolean
  best: { length: number; from: string; to: string } | null
}

export function streaks(days: StepDay[], today: string): Streaks {
  const byDay = new Map(days.map((d) => [d.day, d]))
  const met = (day: string) => {
    const d = byDay.get(day)
    return d !== undefined && goalMet(d)
  }
  const todayIncluded = met(today)
  let current = 0
  for (let day = todayIncluded ? today : addDays(today, -1); met(day); day = addDays(day, -1)) current += 1

  let best: Streaks['best'] = null
  let run = null as Streaks['best']
  for (const d of days) {
    if (d.day > today) break
    if (!goalMet(d)) {
      run = null
      continue
    }
    run = run && addDays(run.to, 1) === d.day
      ? { length: run.length + 1, from: run.from, to: d.day }
      : { length: 1, from: d.day, to: d.day }
    if (!best || run.length > best.length) best = run
  }
  return { current, todayIncluded, best }
}

/** Понедельник недели, в которую входит день. */
export function weekStart(day: string): string {
  const shift = (parseIsoDate(day).getDay() + 6) % 7
  return addDays(day, -shift)
}

export type Records = {
  day: { day: string; steps: number } | null
  week: { start: string; steps: number } | null
  month: { month: string; steps: number } | null
  total: number
  wanDays: number
}

function bestGroup(days: StepDay[], key: (d: StepDay) => string): { key: string; steps: number } | null {
  const sums = new Map<string, number>()
  for (const d of days) sums.set(key(d), (sums.get(key(d)) ?? 0) + d.steps)
  let best: { key: string; steps: number } | null = null
  for (const [k, steps] of sums) if (!best || steps > best.steps) best = { key: k, steps }
  return best
}

/** Рекорды за всё время: день, неделя (пн–вс), месяц, сумма и дни от 10 000 (万). */
export function records(days: StepDay[]): Records {
  let day: Records['day'] = null
  for (const d of days) if (!day || d.steps > day.steps) day = { day: d.day, steps: d.steps }
  const week = bestGroup(days, (d) => weekStart(d.day))
  const month = bestGroup(days, (d) => d.day.slice(0, 7))
  return {
    day,
    week: week && { start: week.key, steps: week.steps },
    month: month && { month: month.key, steps: month.steps },
    total: days.reduce((sum, d) => sum + d.steps, 0),
    wanDays: days.filter((d) => d.steps >= WAN_STEPS).length,
  }
}

/** «окт 2026» из 'YYYY-MM'. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  return `${MONTHS_SHORT[m - 1]} ${y}`
}

export const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const
const WEEKDAY_DATIVE = ['понедельникам', 'вторникам', 'средам', 'четвергам', 'пятницам', 'субботам', 'воскресеньям']

export const RHYTHM_WEEKS = 12

export type Rhythm = {
  /** Пн..Вс: среднее за последние RHYTHM_WEEKS недель; дней нет — null. */
  days: { label: string; average: number | null; count: number }[]
  /** Индексы дня недели с наибольшим и наименьшим средним (если таких дней хотя бы два). */
  most: number | null
  least: number | null
}

export function weekdayRhythm(days: StepDay[], today: string, weeks = RHYTHM_WEEKS): Rhythm {
  const from = addDays(today, -(weeks * 7 - 1))
  const sums = Array.from({ length: 7 }, () => ({ total: 0, count: 0 }))
  for (const d of days) {
    if (d.day < from || d.day > today) continue
    const slot = sums[(parseIsoDate(d.day).getDay() + 6) % 7]!
    slot.total += d.steps
    slot.count += 1
  }
  const result = sums.map((s, i) => ({
    label: WEEKDAY_SHORT[i]!,
    average: s.count ? Math.round(s.total / s.count) : null,
    count: s.count,
  }))
  const filled = result.map((r, i) => ({ ...r, i })).filter((r) => r.average !== null)
  if (filled.length < 2) return { days: result, most: null, least: null }
  const sorted = [...filled].sort((a, b) => b.average! - a.average!)
  const most = sorted[0]!
  const least = sorted[sorted.length - 1]!
  return most.average === least.average ? { days: result, most: null, least: null } : { days: result, most: most.i, least: least.i }
}

/** «по средам» — для подписи ритма. */
export const weekdayDative = (index: number) => `по ${WEEKDAY_DATIVE[index]}`

export type CalendarCell = {
  day: string
  steps: number | null
  goal: number | null
  /** 0 — нет данных, 1 — меньше половины цели, 2 — до цели, 3 — цель, 4 — полторы цели и больше. */
  level: 0 | 1 | 2 | 3 | 4
  wan: boolean
  future: boolean
}

export type Calendar = {
  /** Колонки-недели (пн–вс), последняя — текущая неделя. */
  weeks: CalendarCell[][]
  /** Подписи месяцев над колонкой, где начинается месяц. */
  months: { column: number; label: string }[]
  wanDays: number
  goalDays: number
  days: number
}

export const CALENDAR_WEEKS = 53

export function stepLevel(steps: number | null, goal: number | null): CalendarCell['level'] {
  if (steps === null || steps <= 0) return 0
  const ratio = steps / (goal && goal > 0 ? goal : DEFAULT_GOAL)
  if (ratio >= 1.5) return 4
  if (ratio >= 1) return 3
  if (ratio >= 0.5) return 2
  return 1
}

/** Год по неделям, как в GitHub: колонки — недели, строки — дни недели с понедельника. */
export function calendar(days: StepDay[], today: string, weeks = CALENDAR_WEEKS): Calendar {
  const byDay = new Map(days.map((d) => [d.day, d]))
  const first = addDays(weekStart(today), -(weeks - 1) * 7)
  const columns: CalendarCell[][] = []
  const months: Calendar['months'] = []
  let wanDays = 0
  let goalDays = 0
  let counted = 0
  const cursor = parseIsoDate(first)
  for (let w = 0; w < weeks; w++) {
    const column: CalendarCell[] = []
    for (let i = 0; i < 7; i++) {
      const day = toIsoDate(cursor)
      cursor.setDate(cursor.getDate() + 1)
      const d = byDay.get(day)
      const future = day > today
      const cell: CalendarCell = {
        day,
        steps: d && !future ? d.steps : null,
        goal: d && !future ? d.goal : null,
        level: d && !future ? stepLevel(d.steps, d.goal) : 0,
        wan: !!d && !future && d.steps >= WAN_STEPS,
        future,
      }
      if (cell.steps !== null) {
        counted += 1
        if (cell.wan) wanDays += 1
        if (d && goalMet(d)) goalDays += 1
      }
      if (day.endsWith('-01') && (w > 0 || i === 0)) months.push({ column: w, label: MONTHS_SHORT[parseIsoDate(day).getMonth()]! })
      column.push(cell)
    }
    columns.push(column)
  }
  return { weeks: columns, months, wanDays, goalDays, days: counted }
}
